// Android's animated scrollToOffset uses a native ValueAnimator. An immediate
// scrollToOffset only aborts the OverScroller, so it cannot stop that animator
// from writing another offset on its next frame (RN 0.79 ReactScrollView).
// Keep automatic chat positioning on one measured, non-animated path instead
// of racing an animated send with layout/streaming corrections.
export function createAndroidChatScroller({
  getList,
  getMetrics,
  requestFrame = callback => requestAnimationFrame(callback),
  cancelFrame = handle => cancelAnimationFrame(handle),
}) {
  let frame = null;
  let generation = 0;
  let lastScroll = null;

  const cancel = () => {
    generation += 1;
    if (frame !== null) cancelFrame(frame);
    frame = null;
    lastScroll = null;
  };

  const scrollToBottom = () => {
    if (frame !== null) return;
    const requestedGeneration = generation;
    frame = requestFrame(() => {
      if (generation !== requestedGeneration) return;
      frame = null;
      const { contentHeight, layoutHeight, offset, blocked } = getMetrics();
      const list = getList();
      if (blocked || !list?.scrollToOffset) return;
      if (![contentHeight, layoutHeight, offset].every(Number.isFinite)
        || contentHeight <= 0 || layoutHeight <= 0) return;

      // Read the latest layout at execution time: a keyboard resize and many
      // token/row measurements can all arrive before this frame runs.
      const target = Math.max(0, contentHeight - layoutHeight);
      if (Math.abs(target - offset) <= 1) return;
      // Native scroll events can lag behind commands. Do not resend the same
      // target while waiting for acknowledgement, including subpixel reflows.
      if (lastScroll?.list === list && Math.abs(lastScroll.target - target) <= 1) return;
      lastScroll = { list, target };
      try {
        list.scrollToOffset({ offset: target, animated: false });
      } catch (_) {
        // The native view may disappear between measurement and dispatch.
        lastScroll = null;
      }
    });
  };

  return { scrollToBottom, cancel };
}
